import {
  OFFICE_TIME_ZONE,
  compareTasksByDue,
  dateOnlyKey,
  formatTaskDueTime,
  isChecklistItemDone,
  isTaskDueTime,
  shownTaskDueTime,
  type Checklist,
  type ChecklistItem,
} from '@peles/shared';

/**
 * A task list as a page to print (or save as a PDF from the print dialog):
 * every task in date-and-time order, grouped under the day it is due, with a
 * box to tick on paper. Built as a page for `printDocument`.
 */

export interface TaskListPrintOptions {
  /** Finished tasks too — off, the page is what is left to do. */
  includeDone: boolean;
  /** Each task's notes under its title. */
  includeNotes: boolean;
  /** The viewer's today (YYYY-MM-DD), to mark the days already past. */
  todayKey: string;
  /** When the page was printed, for its footer. */
  printedAt?: Date;
}

const STATUS_LABELS: Record<ChecklistItem['status'], string> = {
  TODO: 'To do',
  IN_PROGRESS: 'In progress',
  DONE: 'Done',
  BLOCKED: 'Blocked',
};

function esc(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** "Tuesday, November 4, 2026" for a YYYY-MM-DD. */
function dayHeading(key: string): string {
  return new Date(`${key}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export interface PrintedTask {
  task: ChecklistItem;
  sectionName: string;
}

/** "Event day", "7 days before the event", "1 day after the event". */
export function offsetHeading(days: number): string {
  if (days === 0) return 'Event day';
  const n = Math.abs(days);
  return `${n} day${n === 1 ? '' : 's'} ${days < 0 ? 'before' : 'after'} the event`;
}

/**
 * The time a template task prints with: a template is dated by its offset
 * from the event, not a date, so it needs an offset rather than a due date.
 */
function templateTime(task: ChecklistItem, checklist: Checklist): string | null {
  if (!checklist.dueTimeEnabled || task.dueDateOffsetDays == null) return null;
  return isTaskDueTime(task.dueTime) ? task.dueTime : null;
}

/** Template tasks by offset (earliest first), then time, undated last. */
function compareByOffset(a: ChecklistItem, b: ChecklistItem): number {
  const ao = a.dueDateOffsetDays;
  const bo = b.dueDateOffsetDays;
  if (ao == null || bo == null) return ao != null ? -1 : bo != null ? 1 : 0;
  if (ao !== bo) return ao - bo;
  const at = a.dueTime ?? '';
  const bt = b.dueTime ?? '';
  return at === bt ? 0 : at < bt ? -1 : 1;
}

/**
 * The list's tasks as they print: in date-and-time order — a template's by its
 * offset from the event — sections' order breaking ties.
 */
export function taskListPrintRows(checklist: Checklist, includeDone: boolean): PrintedTask[] {
  const rows = checklist.sections.flatMap((section) =>
    section.items
      .filter((task) => includeDone || !isChecklistItemDone(task))
      .map((task) => ({
        task: {
          ...task,
          dueTime: checklist.isTemplate
            ? templateTime(task, checklist)
            : shownTaskDueTime(task, checklist),
        },
        sectionName: section.name,
      })),
  );
  return rows.sort((a, b) =>
    checklist.isTemplate ? compareByOffset(a.task, b.task) : compareTasksByDue(a.task, b.task),
  );
}

/** The heading a task prints under: its day, or a template's offset from the event. */
function groupKey(task: ChecklistItem, isTemplate: boolean): string | null {
  if (isTemplate) return task.dueDateOffsetDays == null ? null : String(task.dueDateOffsetDays);
  return task.dueDate ? dateOnlyKey(task.dueDate) : null;
}

export function taskListTitle(checklist: Checklist): string {
  return (checklist.isTemplate ? checklist.templateName : null) || checklist.title || 'Task list';
}

export function buildTaskListPrintHtml(
  checklist: Checklist,
  options: TaskListPrintOptions,
): string {
  const title = taskListTitle(checklist);
  const rows = taskListPrintRows(checklist, options.includeDone);
  const showSection = checklist.sections.length > 1;
  const timed = checklist.dueTimeEnabled;
  const eventLine = checklist.event
    ? `${esc(checklist.event.name)} · ${esc(
        new Date(checklist.event.startAt).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: OFFICE_TIME_ZONE,
        }),
      )}`
    : '';

  const groups: { key: string | null; rows: PrintedTask[] }[] = [];
  for (const row of rows) {
    const key = groupKey(row.task, checklist.isTemplate);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.rows.push(row);
    else groups.push({ key, rows: [row] });
  }

  const body = groups
    .map((group) => {
      const past = !checklist.isTemplate && group.key !== null && group.key < options.todayKey;
      const heading =
        group.key === null
          ? 'No due date'
          : checklist.isTemplate
            ? offsetHeading(Number(group.key))
            : `${dayHeading(group.key)}${group.key === options.todayKey ? ' · Today' : ''}`;
      const items = group.rows
        .map(({ task, sectionName }) => {
          const done = isChecklistItemDone(task);
          const who = task.assignedToName || task.assignee;
          const status = !done && task.status !== 'TODO' ? STATUS_LABELS[task.status] : '';
          const meta = [showSection ? sectionName : '', who ? `Assigned to ${who}` : '', status]
            .filter(Boolean)
            .map(esc)
            .join(' · ');
          return `<tr class="${done ? 'done' : ''}">
  <td class="box"><span class="tick">${done ? '&#10003;' : ''}</span></td>
  ${timed ? `<td class="time">${esc(formatTaskDueTime(task.dueTime) ?? '')}</td>` : ''}
  <td class="task"><div class="title">${esc(task.title)}</div>${
    meta ? `<div class="meta">${meta}</div>` : ''
  }${options.includeNotes && task.notes ? `<div class="notes">${esc(task.notes)}</div>` : ''}</td>
</tr>`;
        })
        .join('\n');
      return `<section class="day">
<h2 class="${past ? 'past' : ''}">${esc(heading)}${past ? ' <span class="late">Past due</span>' : ''}</h2>
<table>${items}</table>
</section>`;
    })
    .join('\n');

  const total = rows.length;
  const doneCount = rows.filter((r) => isChecklistItemDone(r.task)).length;
  const printed = (options.printedAt ?? new Date()).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  @page { size: letter; margin: 0.6in; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif; color: #111827; margin: 0; font-size: 12px; }
  header { border-bottom: 2px solid #111827; padding-bottom: 8px; margin-bottom: 14px; }
  h1 { font-size: 22px; margin: 0; }
  .sub { color: #4b5563; font-size: 13px; margin-top: 3px; }
  .count { color: #6b7280; font-size: 11px; margin-top: 4px; }
  .day { margin-bottom: 14px; break-inside: avoid-page; }
  h2 { font-size: 13px; margin: 0 0 4px; padding: 4px 8px; background: #f3f4f6; border-radius: 4px; }
  h2.past { background: #fef2f2; color: #991b1b; }
  .late { font-weight: normal; font-size: 11px; margin-left: 6px; }
  table { width: 100%; border-collapse: collapse; }
  tr { break-inside: avoid; }
  td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  td.box { width: 26px; }
  .tick { display: inline-block; width: 14px; height: 14px; border: 1.5px solid #374151; border-radius: 3px; text-align: center; line-height: 12px; font-size: 11px; }
  td.time { width: 72px; white-space: nowrap; font-weight: 600; color: #374151; }
  .title { font-size: 13px; }
  .meta { color: #6b7280; font-size: 11px; margin-top: 2px; }
  .notes { color: #374151; font-size: 11px; margin-top: 3px; font-style: italic; white-space: pre-wrap; }
  tr.done .title { text-decoration: line-through; color: #9ca3af; }
  .empty { color: #6b7280; padding: 24px 0; text-align: center; }
  footer { margin-top: 18px; color: #9ca3af; font-size: 10px; }
</style></head>
<body>
<header>
  <h1>${esc(title)}</h1>
  ${eventLine ? `<div class="sub">${eventLine}</div>` : ''}
  <div class="count">${total} task${total === 1 ? '' : 's'}${
    options.includeDone ? ` · ${doneCount} done` : ' still to do'
  }</div>
</header>
${body || '<p class="empty">No tasks to print.</p>'}
<footer>Printed ${esc(printed)}</footer>
</body></html>`;
}
